import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/hooks/useCart';
import { sendPaymentSuccessEmail, generateOrderId } from '@/lib/sendPaymentEmail';

// Deterministic QR pattern
function QRPattern({ seed, size = 120 }: { seed: number; size?: number }) {
  const cells = 21;
  const pattern = Array.from({ length: cells * cells }, (_, i) => {
    const row = Math.floor(i / cells);
    const col = i % cells;
    const inFinder = (
      (row < 7 && col < 7) ||
      (row < 7 && col >= cells - 7) ||
      (row >= cells - 7 && col < 7)
    );
    if (inFinder) {
      const localR = row < 7 ? row : row - (cells - 7);
      const localC = col < 7 ? col : col - (cells - 7);
      return (localR === 0 || localR === 6 || localC === 0 || localC === 6 || (localR >= 2 && localR <= 4 && localC >= 2 && localC <= 4));
    }
    return ((i * 7 + seed * 13 + row * 3 + col * 5) % 3 !== 0);
  });
  const cellSize = size / cells;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg">
      {pattern.map((filled, i) => {
        if (!filled) return null;
        const row = Math.floor(i / cells);
        const col = i % cells;
        return <rect key={i} x={col * cellSize} y={row * cellSize} width={cellSize} height={cellSize} fill="#1a1a1a" />;
      })}
    </svg>
  );
}

export default function CartPage() {
  const { items, removeItem, updateQty, clearCart, totalCount, totalPrice } = useCart();
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set(items.map(i => `${i.id}-${i.spec}`)));
  const [showCheckout, setShowCheckout] = useState(false);
  const [payMethod, setPayMethod] = useState<'wechat' | 'alipay'>('wechat');
  const [paying, setPaying] = useState(false);
  const [orderDone, setOrderDone] = useState(false);
  const [address, setAddress] = useState('');
  const [receiptEmail, setReceiptEmail] = useState('');
  const [lastOrderId, setLastOrderId] = useState('');
  const [qrCountdown, setQrCountdown] = useState(300);
  const navigate = useNavigate();

  useEffect(() => {
    if (showCheckout) {
      setQrCountdown(300);
      const timer = setInterval(() => {
        setQrCountdown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showCheckout, payMethod]);

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const toggleCheck = (key: string) => {
    setCheckedIds(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const toggleAll = () => {
    if (checkedIds.size === items.length) setCheckedIds(new Set());
    else setCheckedIds(new Set(items.map(i => `${i.id}-${i.spec}`)));
  };

  const checkedItems = items.filter(i => checkedIds.has(`${i.id}-${i.spec}`));
  const checkedTotal = checkedItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const checkedCount = checkedItems.reduce((s, i) => s + i.quantity, 0);

  const handlePay = async () => {
    if (!address.trim()) return;
    setPaying(true);
    await new Promise(r => setTimeout(r, 1800));
    const newOrderId = generateOrderId('SHP');
    setLastOrderId(newOrderId);
    if (receiptEmail.trim()) {
      const itemNames = checkedItems.map(i => `${i.name}×${i.quantity}`).join('、');
      await sendPaymentSuccessEmail({
        email: receiptEmail.trim(),
        orderId: newOrderId,
        itemName: itemNames,
        amount: checkedTotal,
        payMethod,
        type: 'shop',
      });
    }
    setPaying(false);
    setOrderDone(true);
    checkedItems.forEach(i => removeItem(i.id, i.spec));
  };

  if (orderDone) {
    return (
      <div className="min-h-screen bg-[#faf9f7]">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-[70vh]">
          <div className="text-center max-w-sm mx-auto px-4">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
              <i className="ri-checkbox-circle-fill text-green-600 text-4xl"></i>
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-2">订单提交成功！</h2>
            <p className="text-gray-500 text-sm mb-1">预计3-5个工作日内发货</p>
            {lastOrderId && (
              <p className="text-xs text-gray-400 font-mono mb-3">订单号：{lastOrderId}</p>
            )}
            {receiptEmail && (
              <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3 mb-5 flex items-center gap-2 text-left">
                <i className="ri-mail-check-line text-green-600 text-base flex-shrink-0"></i>
                <p className="text-xs text-green-700">订单确认邮件已发送至 <strong>{receiptEmail}</strong></p>
              </div>
            )}
            <p className="text-gray-400 text-xs mb-8">感谢您在金榜旺财商城购物！</p>
            <div className="flex gap-3 justify-center">
              <Link to="/shop" className="px-6 py-3 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">
                继续购物
              </Link>
              <Link to="/orders" className="px-6 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-full cursor-pointer hover:border-red-300 hover:text-red-600 transition-colors whitespace-nowrap">
                <i className="ri-list-check mr-1"></i>查看订单
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />
      <div className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-black text-gray-900">
              购物车
              {totalCount > 0 && <span className="ml-2 text-base font-normal text-gray-400">({totalCount}件)</span>}
            </h1>
            {items.length > 0 && (
              <button onClick={clearCart} className="text-xs text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                清空购物车
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="text-center py-24">
              <i className="ri-shopping-cart-line text-6xl text-gray-200 mb-5 block"></i>
              <p className="text-gray-400 text-base mb-6">购物车还是空的，快去挑选商品吧！</p>
              <Link to="/shop" className="px-8 py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap inline-block">
                去商城逛逛
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Cart Items */}
              <div className="lg:col-span-2 flex flex-col gap-3">
                {/* Select All */}
                <div className="bg-white rounded-2xl px-5 py-3 flex items-center gap-3">
                  <button
                    onClick={toggleAll}
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all flex-shrink-0 ${
                      checkedIds.size === items.length ? 'bg-red-600 border-red-600' : 'border-gray-300'
                    }`}
                  >
                    {checkedIds.size === items.length && <i className="ri-check-line text-white text-xs"></i>}
                  </button>
                  <span className="text-sm text-gray-600">全选</span>
                  <span className="text-xs text-gray-400 ml-auto">共 {items.length} 种商品</span>
                </div>

                {items.map(item => {
                  const key = `${item.id}-${item.spec}`;
                  const checked = checkedIds.has(key);
                  return (
                    <div key={key} className={`bg-white rounded-2xl p-5 transition-all ${checked ? '' : 'opacity-60'}`}>
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <button
                          onClick={() => toggleCheck(key)}
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all flex-shrink-0 mt-1 ${
                            checked ? 'bg-red-600 border-red-600' : 'border-gray-300'
                          }`}
                        >
                          {checked && <i className="ri-check-line text-white text-xs"></i>}
                        </button>

                        {/* Image */}
                        <Link to={`/shop/${item.id}`} className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                        </Link>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <Link to={`/shop/${item.id}`} className="font-semibold text-gray-900 text-sm line-clamp-2 hover:text-red-600 transition-colors cursor-pointer block">
                            {item.name}
                          </Link>
                          <p className="text-xs text-gray-400 mt-1">{item.merchant}</p>
                          <p className="text-xs text-gray-400">规格：{item.spec}</p>
                          <div className="flex items-center justify-between mt-3">
                            <div>
                              <span className="text-red-600 font-bold">¥{item.price}</span>
                              <span className="text-gray-300 text-xs line-through ml-1">¥{item.originalPrice}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Qty */}
                              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                  onClick={() => updateQty(item.id, item.spec, item.quantity - 1)}
                                  className="w-7 h-7 flex items-center justify-center hover:bg-gray-50 cursor-pointer text-gray-500 text-sm"
                                >
                                  <i className="ri-subtract-line"></i>
                                </button>
                                <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                                <button
                                  onClick={() => updateQty(item.id, item.spec, item.quantity + 1)}
                                  className="w-7 h-7 flex items-center justify-center hover:bg-gray-50 cursor-pointer text-gray-500 text-sm"
                                >
                                  <i className="ri-add-line"></i>
                                </button>
                              </div>
                              <button
                                onClick={() => removeItem(item.id, item.spec)}
                                className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 cursor-pointer transition-colors"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl p-6 sticky top-24">
                  <h3 className="font-bold text-gray-900 mb-4">订单摘要</h3>
                  <div className="flex flex-col gap-2 mb-4">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>已选商品 ({checkedCount}件)</span>
                      <span>¥{checkedTotal.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>运费</span>
                      <span className="text-green-600">免费</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>优惠</span>
                      <span className="text-red-600">-¥0</span>
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-4 mb-5">
                    <div className="flex justify-between items-baseline">
                      <span className="font-bold text-gray-900">合计</span>
                      <span className="text-2xl font-black text-red-600">¥{checkedTotal.toFixed(1)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => checkedItems.length > 0 && setShowCheckout(true)}
                    disabled={checkedItems.length === 0}
                    className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    结算 ({checkedCount}件)
                  </button>
                  <Link to="/shop" className="block text-center text-sm text-gray-400 hover:text-red-600 mt-3 cursor-pointer transition-colors">
                    继续购物
                  </Link>

                  {/* Guarantees */}
                  <div className="mt-5 flex flex-col gap-2 border-t border-gray-50 pt-4">
                    {[
                      { icon: 'ri-shield-check-line', text: '正品保障' },
                      { icon: 'ri-truck-line', text: '全国包邮' },
                      { icon: 'ri-refresh-line', text: '7天无理由退换' },
                    ].map(g => (
                      <div key={g.text} className="flex items-center gap-2 text-xs text-gray-400">
                        <i className={`${g.icon} text-green-500`}></i>
                        {g.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-900 text-lg">确认结算</h3>
              <button onClick={() => setShowCheckout(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            {/* Items Summary */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4 max-h-40 overflow-y-auto">
              {checkedItems.map(item => (
                <div key={`${item.id}-${item.spec}`} className="flex items-center gap-3 mb-3 last:mb-0">
                  <img src={item.image} alt={item.name} className="w-10 h-10 rounded-lg object-cover object-top flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 line-clamp-1">{item.name}</p>
                    <p className="text-xs text-gray-400">{item.spec} × {item.quantity}</p>
                  </div>
                  <span className="text-sm font-bold text-red-600 whitespace-nowrap">¥{(item.price * item.quantity).toFixed(1)}</span>
                </div>
              ))}
            </div>

            {/* Address */}
            <div className="mb-3">
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">收货地址 *</label>
              <textarea
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="请输入详细收货地址（姓名、电话、地址）"
                className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none resize-none"
                rows={3}
                maxLength={200}
              />
            </div>

            {/* Email for receipt */}
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">接收订单确认邮件（选填）</label>
              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-mail-line text-gray-400 text-sm"></i>
                <input
                  type="email"
                  name="email"
                  value={receiptEmail}
                  onChange={e => setReceiptEmail(e.target.value)}
                  placeholder="填写邮箱，支付成功后发送确认"
                  className="flex-1 bg-transparent text-sm outline-none"
                />
              </div>
            </div>

            {/* Pay Method */}
            <div className="mb-4">
              <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">支付方式</p>
              <div className="flex gap-2">
                {[
                  { key: 'wechat', label: '微信支付', icon: 'ri-wechat-pay-fill', color: 'text-green-600', activeBorder: 'border-green-500', activeBg: 'bg-green-50' },
                  { key: 'alipay', label: '支付宝', icon: 'ri-alipay-fill', color: 'text-sky-500', activeBorder: 'border-sky-400', activeBg: 'bg-sky-50' },
                ].map(m => (
                  <button
                    key={m.key}
                    onClick={() => setPayMethod(m.key as typeof payMethod)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all cursor-pointer ${payMethod === m.key ? `${m.activeBorder} ${m.activeBg}` : 'border-gray-200 bg-white'}`}
                  >
                    <i className={`${m.icon} text-lg ${payMethod === m.key ? m.color : 'text-gray-300'}`}></i>
                    <span className={`text-sm font-semibold ${payMethod === m.key ? 'text-gray-800' : 'text-gray-400'}`}>{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* QR Code Display */}
            <div className={`rounded-2xl p-4 border ${payMethod === 'wechat' ? 'bg-green-50 border-green-100' : 'bg-sky-50 border-sky-100'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <i className={`${payMethod === 'wechat' ? 'ri-wechat-pay-fill text-green-600' : 'ri-alipay-fill text-sky-500'} text-base`}></i>
                  <span className="text-sm font-semibold text-gray-700">{payMethod === 'wechat' ? '微信扫码支付' : '支付宝扫码支付'}</span>
                </div>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {qrCountdown > 0 ? formatCountdown(qrCountdown) : '已过期'}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className={`relative p-2 bg-white border-2 rounded-xl flex-shrink-0 ${qrCountdown === 0 ? 'opacity-40 border-gray-200' : payMethod === 'wechat' ? 'border-green-200' : 'border-sky-200'}`}>
                  <QRPattern seed={Math.round(checkedTotal) + (payMethod === 'wechat' ? 42 : 88)} size={80} />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className={`w-5 h-5 flex items-center justify-center rounded-md ${payMethod === 'wechat' ? 'bg-green-500' : 'bg-sky-500'}`}>
                      <i className={`${payMethod === 'wechat' ? 'ri-wechat-pay-fill' : 'ri-alipay-fill'} text-white text-xs`}></i>
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800 mb-1">
                    打开{payMethod === 'wechat' ? '微信' : '支付宝'}扫码
                  </p>
                  <p className="text-xs text-gray-500 mb-2">支付金额：<strong className="text-red-600">¥{checkedTotal.toFixed(1)}</strong></p>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                    <p className="text-xs text-amber-700">演示模式，正式上线后显示真实二维码</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between items-center bg-red-50 rounded-xl px-4 py-3">
              <span className="text-sm font-medium text-gray-700">应付总额</span>
              <span className="text-xl font-black text-red-600">¥{checkedTotal.toFixed(1)}</span>
            </div>

            <button
              onClick={handlePay}
              disabled={paying || !address.trim() || qrCountdown === 0}
              className={`w-full py-3.5 text-white font-semibold rounded-full cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${payMethod === 'wechat' ? 'bg-green-600 hover:bg-green-500' : 'bg-sky-500 hover:bg-sky-400'}`}
            >
              {paying ? '确认中...' : qrCountdown === 0 ? '二维码已过期，请刷新' : `我已完成扫码支付`}
            </button>
            {!address.trim() && <p className="text-xs text-red-400 text-center mt-1">请填写收货地址</p>}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
